import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Search, X, Users, Briefcase, Building2, FileText, 
  User, Loader2, ArrowRight
} from 'lucide-react';
import api from '../services/api';
import { debounce } from '../utils/debounce';

const OBJECT_CONFIGS = {
  candidates: {
    icon: Users,
    color: 'text-blue-600 bg-blue-100',
    label: 'Candidate',
    path: '/candidates',
    roles: ['super_admin', 'admin', 'team_lead', 'recruiter']
  },
  jobs: {
    icon: Briefcase,
    color: 'text-emerald-600 bg-emerald-100',
    label: 'Job',
    path: '/jobs',
    roles: ['super_admin', 'admin', 'bde', 'team_lead', 'recruiter']
  },
  clients: {
    icon: Building2,
    color: 'text-purple-600 bg-purple-100',
    label: 'Client',
    path: '/clients',
    roles: ['super_admin', 'admin', 'bde', 'bde_manager']
  },
  workorders: {
    icon: FileText,
    color: 'text-amber-600 bg-amber-100',
    label: 'Workorder',
    path: '/workorders',
    roles: ['super_admin', 'admin', 'operations', 'bde']
  },
  users: {
    icon: User,
    color: 'text-slate-600 bg-slate-100',
    label: 'User',
    path: '/setup',
    roles: ['super_admin', 'admin']
  }
};

export function GlobalSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Get allowed objects based on user role
  const allowedObjects = Object.entries(OBJECT_CONFIGS)
    .filter(([_, config]) => !config.roles || config.roles.includes(user?.role))
    .map(([key]) => key);

  // Debounced search function
  const performSearch = useCallback(
    debounce(async (searchQuery) => {
      if (!searchQuery || searchQuery.length < 2) {
        setResults({});
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await api.get('/search/global', {
          params: { q: searchQuery, objects: allowedObjects.join(',') }
        });
        setResults(res.data || {});
        setSelectedIndex(0);
      } catch (error) {
        console.error('Search failed:', error);
        setResults({});
      } finally {
        setLoading(false);
      }
    }, 300),
    [allowedObjects]
  );

  useEffect(() => {
    performSearch(query);
  }, [query, performSearch]);

  // Keyboard shortcut to open search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Flatten results for keyboard navigation
  const flatResults = Object.entries(results).flatMap(([type, items]) =>
    (items || []).map(item => ({ ...item, _type: type }))
  );

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && flatResults[selectedIndex]) {
      handleSelect(flatResults[selectedIndex]);
    }
  };

  const handleSelect = (item) => {
    const config = OBJECT_CONFIGS[item._type];
    if (config) {
      navigate(`${config.path}/${item.id}`);
    }
    setOpen(false);
    setQuery('');
  };

  const getDisplayName = (item, type) => {
    switch (type) {
      case 'candidates':
        return item.name;
      case 'jobs':
        return item.title;
      case 'clients':
        return item.name;
      case 'workorders':
        return `WO-${item.id?.slice(0, 8)}`;
      case 'users':
        return item.name;
      default:
        return item.name || item.title || item.id;
    }
  };

  const getSubtitle = (item, type) => {
    switch (type) {
      case 'candidates':
        return item.email || item.job_title;
      case 'jobs':
        return item.client_name;
      case 'clients':
        return item.industry;
      case 'workorders':
        return item.client_name;
      case 'users':
        return item.email;
      default:
        return '';
    }
  };

  const totalResults = Object.values(results).reduce((sum, arr) => sum + (arr?.length || 0), 0);

  return (
    <div className="relative" ref={containerRef}>
      {/* Search Trigger */}
      <button
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors w-64"
        data-testid="global-search-trigger"
      >
        <Search className="w-4 h-4" />
        <span className="flex-1 text-left">Search...</span>
        <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-mono bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600">
          ⌘K
        </kbd>
      </button>

      {/* Search Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/50">
          <div className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            {/* Search Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <Search className="w-5 h-5 text-slate-400" />
              <Input
                ref={inputRef}
                type="text"
                placeholder="Search candidates, jobs, clients..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 border-0 focus-visible:ring-0 text-lg placeholder:text-slate-400"
                data-testid="global-search-input"
              />
              {loading && <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />}
              <button onClick={() => { setOpen(false); setQuery(''); }}>
                <X className="w-5 h-5 text-slate-400 hover:text-slate-600" />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto">
              {query.length < 2 ? (
                <div className="px-4 py-8 text-center text-slate-500">
                  <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Type at least 2 characters to search</p>
                  <p className="text-xs mt-2 text-slate-400">
                    Search across {allowedObjects.length} objects
                  </p>
                </div>
              ) : totalResults === 0 && !loading ? (
                <div className="px-4 py-8 text-center text-slate-500">
                  <p>No results found for "{query}"</p>
                </div>
              ) : (
                <div className="py-2">
                  {Object.entries(results).map(([type, items]) => {
                    if (!items || items.length === 0) return null;
                    const config = OBJECT_CONFIGS[type];
                    if (!config) return null;
                    const Icon = config.icon;

                    return (
                      <div key={type} className="mb-2">
                        <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          {config.label}s ({items.length})
                        </div>
                        {items.slice(0, 5).map((item, idx) => {
                          const globalIdx = flatResults.findIndex(
                            r => r.id === item.id && r._type === type
                          );
                          const isSelected = globalIdx === selectedIndex;

                          return (
                            <button
                              key={item.id}
                              onClick={() => handleSelect({ ...item, _type: type })}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${
                                isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                              }`}
                              data-testid={`search-result-${type}-${item.id}`}
                            >
                              <div className={`p-2 rounded-lg ${config.color}`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 dark:text-slate-100 truncate">
                                  {getDisplayName(item, type)}
                                </p>
                                {getSubtitle(item, type) && (
                                  <p className="text-sm text-slate-500 truncate">
                                    {getSubtitle(item, type)}
                                  </p>
                                )}
                              </div>
                              <ArrowRight className="w-4 h-4 text-slate-400" />
                            </button>
                          );
                        })}
                        {items.length > 5 && (
                          <button
                            onClick={() => {
                              navigate(`${config.path}?search=${encodeURIComponent(query)}`);
                              setOpen(false);
                              setQuery('');
                            }}
                            className="w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-left"
                          >
                            View all {items.length} {config.label.toLowerCase()}s →
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-xs text-slate-500 flex items-center gap-4">
              <span><kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 rounded border">↑↓</kbd> Navigate</span>
              <span><kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 rounded border">Enter</kbd> Select</span>
              <span><kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 rounded border">Esc</kbd> Close</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
